import React, { createContext, useContext } from 'react';
import { cx, FOCUS_RING } from './styles';

/**
 * The responsive table/stack primitive (DESIGN_PLAN §4.3).
 *
 * `lg`+ — an aligned 12-column grid with a header row, so the eye can finally
 * scan down a column. Long values `truncate` and carry a `title` with the full
 * value; numeric columns are right-aligned and `tabular-nums`.
 *
 * `< lg` — the header row disappears and each row becomes a stacked card. Never
 * `overflow-x-auto` on a whole table: a horizontally scrolling table inside a
 * vertically scrolling page is the worst option on touch, and every list that
 * uses this has a natural stacked form. Pass `stacked` to control that form.
 */

export interface DataListColumn {
  key: string;
  label: string;
  /** Of 12. The spans of all columns should total 12 (excluding `trailing`). */
  span: number;
  align?: 'left' | 'right';
  /** Extra classes for both the header cell and the body cell. */
  className?: string;
}

const SPAN_CLASS: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

const ColumnsContext = createContext<DataListColumn[]>([]);

export interface DataListProps {
  columns: DataListColumn[];
  /** Reserve a 24px trailing cell (chevron, row action). */
  trailing?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const DataList: React.FC<DataListProps> = ({ columns, trailing, className, children }) => (
  <ColumnsContext.Provider value={columns}>
    <div className={cx('lg:rounded-2xl lg:border lg:border-white/[0.06] lg:bg-white/[0.02]', className)}>
      <div
        aria-hidden="true"
        className="hidden lg:flex lg:items-center lg:gap-3 lg:border-b lg:border-white/[0.06] lg:px-4 lg:py-2.5"
      >
        <div className="grid flex-1 grid-cols-12 gap-3">
          {columns.map((col) => (
            <div
              key={col.key}
              className={cx(
                'text-[11px] uppercase tracking-wider text-white/35',
                SPAN_CLASS[col.span],
                col.align === 'right' && 'text-right',
                col.className,
              )}
            >
              {col.label}
            </div>
          ))}
        </div>
        {trailing && <div className="w-6 flex-none" />}
      </div>
      <div className="space-y-3 lg:space-y-0">{children}</div>
    </div>
  </ColumnsContext.Provider>
);

export interface DataListRowProps {
  /** Desktop cells, in `columns` order. */
  cells: React.ReactNode[];
  /**
   * The `< lg` priority stack. Defaults to the cells rendered as
   * label/value pairs, which is right for short rows and wrong for long ones —
   * pass an explicit stack for anything with more than three fields.
   */
  stacked?: React.ReactNode;
  /** Makes the whole row one `<button type="button">`. */
  onClick?: () => void;
  expanded?: boolean;
  /** Trailing 24px cell content, e.g. a chevron. */
  trailing?: React.ReactNode;
  /** Expanded panel, rendered outside the clickable row. */
  children?: React.ReactNode;
  className?: string;
}

export const DataListRow: React.FC<DataListRowProps> = ({
  cells,
  stacked,
  onClick,
  expanded,
  trailing,
  children,
  className,
}) => {
  const columns = useContext(ColumnsContext);

  const desktop = (
    <div className="hidden lg:grid lg:flex-1 lg:grid-cols-12 lg:items-center lg:gap-3">
      {columns.map((col, i) => (
        <div
          key={col.key}
          className={cx(
            'min-w-0 text-sm text-white/70',
            SPAN_CLASS[col.span],
            col.align === 'right' && 'text-right tabular-nums',
            col.className,
          )}
        >
          {cells[i]}
        </div>
      ))}
    </div>
  );

  const mobile = (
    <div className="space-y-1.5 lg:hidden">
      {stacked ?? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {columns.map((col, i) => (
            <div key={col.key} className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-white/35">{col.label}</div>
              <div className="truncate text-sm text-white/70">{cells[i]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const inner = (
    <>
      {desktop}
      {mobile}
      {trailing && <div className="hidden w-6 flex-none lg:block">{trailing}</div>}
    </>
  );

  const rowShell = cx(
    'w-full rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-left',
    'lg:flex lg:items-center lg:gap-3 lg:rounded-none lg:border-0 lg:border-b lg:border-white/[0.05] lg:bg-transparent lg:px-4 lg:py-3',
    className,
  );

  return (
    <div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-expanded={expanded}
          className={cx(rowShell, 'transition-colors hover:bg-white/[0.04]', FOCUS_RING)}
        >
          {inner}
        </button>
      ) : (
        <div className={rowShell}>{inner}</div>
      )}
      {children}
    </div>
  );
};

/**
 * A truncating cell that keeps the full value reachable via `title`.
 * Use for event names, account identifiers, invoice numbers.
 */
export const DataListCell: React.FC<{
  value?: string | null;
  fallback?: string;
  className?: string;
}> = ({ value, fallback = '—', className }) => (
  <span title={value || undefined} className={cx('block truncate', className)}>
    {value || fallback}
  </span>
);
